import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router';

import { ADMIN_HOME_PATH, ADMIN_LOGIN_PATH } from '@/lib/admin-access';
import { AdminShell } from './admin-shell';
import { LoginRoute } from '@/features/login/login-route';
import { RequireSession } from './require-session';
import { DashboardRoute } from '@/features/dashboard/dashboard-route';
import { DocumentsRoute } from '@/features/documents/documents-route';
import { OrganizationDetailRoute } from '@/features/organizations/organization-detail-route';
import { OrganizationsRoute } from '@/features/organizations/organizations-route';
import { ReportsRoute } from '@/features/reports/reports-route';
import { StatsRoute } from '@/features/reports/stats-route';
import { RolesRoute } from '@/features/roles/roles-route';
import { SettingsRoute } from '@/features/settings/settings-route';
import { SubscriptionsRoute } from '@/features/subscriptions/subscriptions-route';
import { SupportRoute } from '@/features/support/support-route';
import { SupportTicketRoute } from '@/features/support/support-ticket-route';
import { TalentDetailRoute } from '@/features/talents/talent-detail-route';
import { TalentsRoute } from '@/features/talents/talents-route';
import { ModerationDetailRoute } from '@/features/verification/moderation-detail-route';
import { VerificationRoute } from '@/features/verification/verification-route';

const router = createBrowserRouter([
  { path: ADMIN_LOGIN_PATH, element: <LoginRoute /> },
  { path: '/admin/login', element: <Navigate to={ADMIN_LOGIN_PATH} replace /> },
  // Outside the shell: the stats view is shown to visitors, with no console around it.
  {
    path: '/reports/stats',
    element: (
      <RequireSession>
        <StatsRoute />
      </RequireSession>
    ),
  },
  {
    path: ADMIN_HOME_PATH,
    element: (
      <RequireSession>
        <AdminShell />
      </RequireSession>
    ),
    children: [
      { index: true, element: <DashboardRoute /> },
      { path: 'reports', element: <ReportsRoute /> },
      { path: 'verification', element: <VerificationRoute /> },
      // One review screen, mounted under each queue so it inherits that queue's
      // permission: a document auditor opens documents, not registrations.
      { path: 'verification/:itemId', element: <ModerationDetailRoute subject="organization" /> },
      { path: 'documents', element: <DocumentsRoute /> },
      { path: 'documents/:itemId', element: <ModerationDetailRoute subject="document" /> },
      { path: 'support', element: <SupportRoute /> },
      { path: 'support/:ticketId', element: <SupportTicketRoute /> },
      { path: 'talents', element: <TalentsRoute /> },
      { path: 'talents/:candidateId', element: <TalentDetailRoute /> },
      { path: 'organizations', element: <OrganizationsRoute /> },
      { path: 'organizations/:organizationId', element: <OrganizationDetailRoute /> },
      { path: 'subscriptions', element: <SubscriptionsRoute /> },
      { path: 'roles', element: <RolesRoute /> },
      { path: 'settings', element: <SettingsRoute /> },
      { path: 'security', element: <Navigate to="/settings" replace /> },
    ],
  },
  { path: '/admin/*', element: <LegacyAdminRedirect /> },
  { path: '*', element: <Navigate to={ADMIN_HOME_PATH} replace /> },
]);

/**
 * `/admin/reports` → `/reports`, query string and hash intact.
 *
 * The console used to live under `/admin`, and bookmarks and shared links from
 * then still arrive with the prefix. Dropping them all on the dashboard threw
 * away where the person was going; stripping the prefix instead lets
 * `RequireSession` carry the page through the login round trip as `?next=`.
 * `/admin/login` is mapped on its own above, and a path with nothing after the
 * prefix is the dashboard anyway.
 */
export function LegacyAdminRedirect() {
  const { pathname, search, hash } = useLocation();
  const target = stripLegacyAdminPrefix(pathname);

  return <Navigate to={`${target}${search}${hash}`} replace />;
}

export function stripLegacyAdminPrefix(pathname: string): string {
  const stripped = pathname.replace(/^\/admin(?=\/|$)/, '');
  return stripped === '' || stripped === '/' ? ADMIN_HOME_PATH : stripped;
}

export function ConsoleRouter() {
  return <RouterProvider router={router} />;
}
